import { motion } from "framer-motion";
import { useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowDown } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1777106322601-578dc9213ace?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjByb29mJTIwc3VidXJiYW4lMjBob21lfGVufDB8fHx8MTc4NTU1MDkwOXww&ixlib=rb-4.1.0&q=85";
const LOGO = "https://customer-assets-v7afamib.emergentagent.net/job_deductible-to-done/artifacts/puragtdz_Copy%20of%20Herologo.PNG";

const lines = [
  "If you take the time,",
  "we can make your roof",
  "or any broken roof",
  "look fine.",
];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const scrollToQuote = () => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={BG} alt="New roof" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-hero-void/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-hero-void via-hero-void/40 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 w-full">
        <motion.img
          src={LOGO}
          alt="Hero Exteriors"
          data-testid="hero-logo"
          initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:block absolute right-6 top-[-260px] w-52 h-auto rounded-xl bg-white/95 p-3 shadow-[0_0_60px_rgba(220,38,38,0.25)]"
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-hero-silver"
        >
          <span className="h-px w-8 bg-hero-red" /> Catonsville, Maryland
        </motion.div>

        <h1 data-testid="hero-headline" className="font-display uppercase text-white leading-[0.92] text-5xl sm:text-6xl lg:text-8xl max-w-5xl">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 3 ? <span className="text-hero-red">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-8 max-w-xl text-lg text-hero-silver"
        >
          We&apos;re the middle ground between you and your insurance company. Pay only your deductible — we handle the rest and give you a brand-new roof.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.25 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            onClick={scrollToQuote}
            data-testid="hero-inspection-cta"
            className="group inline-flex items-center gap-3 bg-hero-red hover:bg-hero-reddark text-white font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-md transition-colors duration-200"
          >
            Free Roof Inspection
            <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform duration-200" />
          </button>
          <a
            href="tel:4432509046"
            data-testid="hero-call-cta"
            className="inline-flex items-center gap-3 border border-white/25 hover:border-hero-red hover:text-hero-red text-white font-semibold uppercase tracking-wide text-sm px-8 py-4 rounded-md transition-colors duration-200"
          >
            <Phone className="h-4 w-4" /> 443-250-9046
          </a>
        </motion.div>
      </div>
    </section>
  );
}
